import {
  AccountMenu,
  AddUsersIcon,
  CookieIcon,
  Divider,
  FactCheckIcon,
  Header,
  InboxIcon,
  LoginIcon,
  LogoutIcon,
  MenuHeader,
  MenuItem,
  PersonalInformation,
  OutgoingMailIcon,
  PersonIcon,
  PowerSettingsIcon,
  QuestionMarkIcon,
  SettingsIcon,
  ShieldLockIcon,
  Tooltip,
  UserAvatar,
} from "@rothoblaas/design-system";
import { ComponentType, FC, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../context/appContext";
import { useAuthContext } from "../context/authContext";
import useNotificationEntries from "../hooks/useNotificationEntries";
import useI18n from "../hooks/useTranslations";
import { buildDiagnostics } from "../utils/diagnostics";
import { captureScreenshot } from "../utils/screenshot";
import { hasAdminPermissions } from "./adminMenuEntries";
import FeedbackDialog from "./help/FeedbackDialog";
import HelpRequestDialog from "./help/HelpRequestDialog";
import { SupportRequestCapture } from "./help/SupportRequestDialog";
import styles from "./NavBar.module.css";
import NotificationBell from "./NotificationBell";

type NavBarProps = {
  Logo: ComponentType;
  Menu: ComponentType;
  sidebar?: {
    isOpen: boolean;
    toggle: () => void;
    close: () => void;
  };
};

const iamUrl = import.meta.env.VITE_IAM_URL;

const NavBar: FC<NavBarProps> = ({ Logo, Menu, sidebar }) => {
  const i18n = useI18n();
  const { identity, init, permissions } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { scope } = useAppContext();
  const notificationEntries = useNotificationEntries();

  // bumped to remount the header, which closes the account menu
  const [menuResetKey, setMenuResetKey] = useState(0);
  const [supportRequest, setSupportRequest] = useState<SupportRequestCapture & { kind: "help" | "feedback" }>();
  const isCapturing = useRef(false);

  const openSupportRequest = async (kind: "help" | "feedback") => {
    if (isCapturing.current) {
      return;
    }
    isCapturing.current = true;
    // taken first, so the warnings of the screenshot capture itself are not included
    const diagnostics = buildDiagnostics(scope, i18n.translation.languageCode);
    // the account menu (where the mobile entries are) must not appear in the screenshot
    setMenuResetKey((key) => key + 1);
    const screenshot = await captureScreenshot();
    setSupportRequest({ kind, screenshot, diagnostics });
    isCapturing.current = false;
  };

  const displayName = identity
    ? [identity.firstName, identity.lastName].filter(Boolean).join(" ").trim() || identity.name
    : "";
  const job = identity?.job === "ms-graph-job-title" ? identity.msGraphJobTitle : identity?.job;

  return (
    <>
      <Header
        // remount on navigation so the account menu closes after an internal link is clicked
        key={`${location.pathname}-${menuResetKey}`}
        id="sidebar-activation"
        hamburger={sidebar && { isMenuOpen: sidebar.isOpen, onToggle: sidebar.toggle }}
        locale={i18n.translation.languageCode}
        isStaging={import.meta.env.MODE === "staging"}
        title={
          <div className={styles.logo}>
            <Logo />
          </div>
        }
        extra={
          <div className={styles.extra}>
            {identity && (
              <div className={styles.supportButtons}>
                <NotificationBell entries={notificationEntries} onSelect={(path) => navigate(path)} />
                <Tooltip placement="bottom" content={i18n.translation.helpRequests.title}>
                  <button
                    type="button"
                    className={styles.supportButton}
                    aria-label={i18n.translation.helpRequests.title}
                    onClick={() => openSupportRequest("help")}
                  >
                    <QuestionMarkIcon />
                  </button>
                </Tooltip>
                {permissions.canSendFeedback && (
                  <Tooltip placement="bottom" content={i18n.translation.feedbacks.title}>
                    <button
                      type="button"
                      className={styles.supportButton}
                      aria-label={i18n.translation.feedbacks.title}
                      onClick={() => openSupportRequest("feedback")}
                    >
                      <OutgoingMailIcon />
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
            <PersonalInformation firstName={identity?.firstName} lastName={identity?.lastName} job={job ?? undefined} />
          </div>
        }
        avatar={{
          trigger: identity ? (
            <UserAvatar primaryText={identity.firstName ?? displayName} secondaryText={identity.lastName} />
          ) : (
            <PowerSettingsIcon />
          ),
          menu: (
            <AccountMenu>
              <MenuHeader title={displayName} subtitle={identity?.email} />
              <Divider />
              <Menu />
              {/* AccountMenu children don't accept `false`, hence the ternary */}
              {hasAdminPermissions(permissions) ? (
                <>
                  <Divider />
                  <MenuItem
                    icon={<SettingsIcon />}
                    label={i18n.translation.common.administration}
                    href="/admin"
                    onNavigate={navigate}
                  />
                </>
              ) : (
                <></>
              )}
              <Divider />
              <MenuItem
                icon={<PersonIcon />}
                label={i18n.translation.common.myProfile}
                href={iamUrl + "/me"}
                target="_blank"
              />
              <MenuItem
                icon={<CookieIcon />}
                label={i18n.translation.common.cookiePolicy}
                href={iamUrl + "/cookies"}
                target="_blank"
              />
              <MenuItem
                icon={<ShieldLockIcon />}
                label={i18n.translation.common.privacyPolicy}
                href={iamUrl + "/privacy"}
                target="_blank"
              />
              <MenuItem
                icon={<FactCheckIcon />}
                label={i18n.translation.common.termsAndConditions}
                href={iamUrl + "/terms"}
                target="_blank"
              />
              {/* on wider screens these are the buttons next to the personal information */}
              {identity ? (
                <>
                  <MenuItem
                    icon={<QuestionMarkIcon />}
                    label={i18n.translation.helpRequests.title}
                    onClick={() => openSupportRequest("help")}
                    className={styles.mobileOnly}
                  />
                  {permissions.canSendFeedback ? (
                    <MenuItem
                      icon={<OutgoingMailIcon />}
                      label={i18n.translation.feedbacks.title}
                      onClick={() => openSupportRequest("feedback")}
                      className={styles.mobileOnly}
                    />
                  ) : (
                    <></>
                  )}
                  {/* the notifications of the bell */}
                  {notificationEntries.map((entry) => (
                    <MenuItem
                      key={entry.key}
                      icon={entry.isInvite ? <InboxIcon /> : <AddUsersIcon />}
                      label={entry.label}
                      onClick={() => navigate(entry.path)}
                      className={styles.mobileOnly}
                    />
                  ))}
                </>
              ) : (
                <></>
              )}
              <Divider />
              {identity ? (
                <MenuItem
                  icon={<LogoutIcon />}
                  label={i18n.translation.common.logout}
                  href={iamUrl + `/logout?redirect_uri=${encodeURIComponent(window.location.origin)}`}
                  variant="danger"
                />
              ) : (
                <MenuItem
                  icon={<LoginIcon />}
                  label={i18n.translation.common.login}
                  onClick={() => init(true)}
                  variant="danger"
                />
              )}
            </AccountMenu>
          ),
        }}
      />
      {supportRequest?.kind === "help" && (
        <HelpRequestDialog
          screenshot={supportRequest.screenshot}
          diagnostics={supportRequest.diagnostics}
          onClose={() => setSupportRequest(undefined)}
        />
      )}
      {supportRequest?.kind === "feedback" && (
        <FeedbackDialog
          screenshot={supportRequest.screenshot}
          diagnostics={supportRequest.diagnostics}
          onClose={() => setSupportRequest(undefined)}
        />
      )}
    </>
  );
};

export default NavBar;
